import dayjs from "dayjs";
import _ from "lodash";
import React, { useState } from "react";
import styled from "styled-components";

import { ReactComponent as ICON_PREVIEW } from "@assets/svgs/ICON_PREVIEW.svg";
import { Button } from "@components/Button";
import { Icon } from "@components/Icon";
import { Audio } from "@components/PreviewComponents/Audio";
import { Image } from "@components/PreviewComponents/Image";
import { ScrollingText } from "@components/PreviewComponents/ScrollingText";
import { Video } from "@components/PreviewComponents/Video";
import { Website } from "@components/PreviewComponents/Website";
import { YoutubeLive } from "@components/PreviewComponents/YoutubeLive";
import { Text } from "@components/Text";
import {
  COLOR_DARK_GREY,
  COLOR_GREEN,
  COLOR_GREY_1,
  COLOR_WHITE,
} from "@static/COLORS";
import { convertSecondsToTime } from "@utils/convertSecondsToTime";

const Line = styled.div`
  width: 100%;
  height: 1px;
  background: ${COLOR_GREY_1};
  margin-top: 4px;
  margin-bottom: 4px;
`;

const ContainerPrediction = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  flex-direction: column;
  overflow: scroll;
`;

const SectionViewSlotItem = styled.div``;
const SectionViewCampaignItem = styled.div``;

const SectionCampaignName = styled.div`
  display: flex;
  gap: 8px;
`;

const SectionTopicCampaign = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0px;
  box-sizing: border-box;
`;

const SectionTopicCampaignItem = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
`;

const ContainerMedia = styled.div`
  width: 100%;
  display: flex;
  gap: 8px;
  overflow-y: scroll;
`;

const SectionMediaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MediaItem = styled.div`
  width: 140px;
  height: 80px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  background: red;
`;

export const ViewPrediction = ({ timeSlots, expandedHours, campaigns }) => {
  const [expandedCampaign, setExpandedCampaign] = useState(null);

  const campaignActiveInSlot = ({ campaign, slot }) => {
    const [startHour, startMinute] = campaign.start_time.split(":").map(Number);
    const [endHour, endMinute] = campaign.end_time.split(":").map(Number);
    const [slotHour, slotMinute] = slot.split(":").map(Number);

    const startTime = dayjs().set("hour", startHour).set("minute", startMinute);
    const endTime = dayjs().set("hour", endHour).set("minute", endMinute);
    const slotTime = dayjs().set("hour", slotHour).set("minute", slotMinute);

    return (
      (slotTime.isSame(startTime) || slotTime.isAfter(startTime)) &&
      (slotTime.isBefore(endTime) || slotTime.isSame(endTime))
    );
  };

  const currentCampaign = _.chain(campaigns)
    .filter((filterCampaignItem) => {
      return campaignActiveInSlot({
        campaign: filterCampaignItem,
        slot: `${expandedHours}:00`,
      });
    })
    .value();

  const onHandleRedirect = ({ campaignId }) => {
    window.open(`/preview-campaign/${campaignId}`, "_blank");
  };

  return (
    <ContainerPrediction>
      <SectionTopicCampaign>
        {_.map(currentCampaign, (item, index) => {
          const textColor =
            index === expandedCampaign ? COLOR_WHITE : "#219679";
          const buttonColor =
            index === expandedCampaign ? COLOR_GREEN : "#CCEEE6";
          return (
            <React.Fragment key={index}>
              <SectionTopicCampaignItem>
                <Button
                  width="100%"
                  color={buttonColor}
                  onClick={() =>
                    setExpandedCampaign((prev) =>
                      prev === index ? null : index
                    )
                  }
                >
                  <SectionCampaignName>
                    <Text fontWeight="600" color={textColor}>
                      Campaign
                    </Text>
                    <Text fontWeight="500" color={textColor}>
                      {_.get(item, ["name"])}
                    </Text>
                  </SectionCampaignName>
                </Button>
                <Icon
                  isCursor
                  width={30}
                  height={30}
                  onClick={() =>
                    onHandleRedirect({ campaignId: _.get(item, ["id"]) })
                  }
                >
                  <ICON_PREVIEW />
                </Icon>
              </SectionTopicCampaignItem>
              {expandedCampaign === index && (
                <ContainerMedia>
                  {_.chain(item)
                    .get(["adsBlocks"])
                    .map((adsBlockItem) => {
                      return _.get(adsBlockItem, ["blockMedias"]);
                    })
                    .flatMap()
                    .map((mediaItem, mediaIndex) => {
                      const type = _.get(mediaItem, ["type"]);
                      const attribute = _.get(mediaItem, ["attribute"]);
                      const id = _.get(mediaItem, ["id"]);
                      return (
                        <SectionMediaItem key={mediaIndex}>
                          <MediaItem>
                            {(() => {
                              switch (type) {
                                case "image":
                                  return (
                                    <Image key={id} attribute={attribute} />
                                  );
                                case "video":
                                  return (
                                    <Video
                                      key={id}
                                      attribute={attribute}
                                      mute={true}
                                    />
                                  );
                                case "audio":
                                  return (
                                    <Audio
                                      key={id}
                                      attribute={attribute}
                                      playing={false}
                                    />
                                  );
                                case "website":
                                  return (
                                    <Website key={id} attribute={attribute} />
                                  );
                                case "youtube-live":
                                  return (
                                    <YoutubeLive
                                      key={id}
                                      attribute={attribute}
                                      width={120}
                                      height={80}
                                      playing={false}
                                    />
                                  );
                                case "running-text":
                                  return (
                                    <ScrollingText
                                      key={id}
                                      attribute={attribute}
                                    />
                                  );

                                default:
                                  return null;
                              }
                            })()}
                          </MediaItem>
                          <Text
                            align="center"
                            color={COLOR_DARK_GREY}
                            fontWeight="500"
                          >
                            {convertSecondsToTime({
                              seconds: _.get(mediaItem, ["duration"]),
                            })}
                          </Text>
                        </SectionMediaItem>
                      );
                    })
                    .value()}
                </ContainerMedia>
              )}
            </React.Fragment>
          );
        })}
        <Line />
      </SectionTopicCampaign>

      {_.chain(timeSlots)
        .get([expandedHours])
        .map((item, index) => {
          return (
            <SectionViewSlotItem key={index}>
              <Text color={COLOR_GREEN} fontWeight="500" fontSize="16px">
                {item}
              </Text>
              <SectionViewCampaignItem>
                {_.chain(campaigns)
                  .filter((filterCampaignItem) => {
                    return campaignActiveInSlot({
                      campaign: filterCampaignItem,
                      slot: item,
                    });
                  })
                  .map((mapCampaignItem, mapCampaignIndex) => {
                    return (
                      <SectionCampaignName key={mapCampaignIndex}>
                        <Text fontWeight="600" color={COLOR_DARK_GREY}>
                          Campaign
                        </Text>
                        <Text fontWeight="400">
                          {_.get(mapCampaignItem, ["name"])}
                        </Text>
                      </SectionCampaignName>
                    );
                  })
                  .value()}
              </SectionViewCampaignItem>
              {index !==
                _.chain(timeSlots)
                  .get([expandedHours])
                  .size()
                  .thru((size) => {
                    return size - 1;
                  })
                  .value() && <Line />}
            </SectionViewSlotItem>
          );
        })
        .value()}
    </ContainerPrediction>
  );
};
